import {
  AnyAction,
  CombinedState,
  combineReducers,
  configureStore,
  isRejectedWithValue,
  Middleware,
} from '@reduxjs/toolkit';
import {
  shallowEqual,
  TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from 'react-redux';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import toast from 'components/Base/Notification';
import { EText } from 'data/datas/genericData';
import { isTransportError } from 'helpers/apiError';
import authSlice, { TAuthSliceState } from './slices/auth.slice';
import appSlice, { TAppSliceState } from './slices/app.slice';
import { tenantApi } from './apiSlice/tenant.slices';
import { animalApi } from './apiSlice/animal.slice';
import { paymentApi } from './apiSlice/payment.slice';
import { docPacketApi } from './apiSlice/docPacket.slice';
import { animalTrainingApi } from './apiSlice/animalTraining.slice';
import { leasePacketApi } from './apiSlice/leasePacket.slice';
import { residencyApi } from './apiSlice/residency.slice';
import { policyApi } from './apiSlice/policy.slice';
import { storageApi } from './apiSlice/storage.slice';
import { usersApi } from './apiSlice/users.slice';
import { fileApi } from './apiSlice/file.slice';
import { curriculumApi } from './apiSlice/curriculum.slice';
import { visitationApi } from './apiSlice/visitation.slice';
import { AuthAmplify } from './apiSlice/authAmplify.slice';
import { AuthAmplifyWithPhoneNumber } from './apiSlice/authAmplifyWithPhoneNumber.slice';
import { AuthAmplifyWithEmail } from './apiSlice/authAmplifyWithEmail.slice';
import userSlice from './slices/user.slice';
import { applicationsApi } from './apiSlice/application.slice';
import { complaintApi } from './apiSlice/complaint.slice';
import { noticeApi } from './apiSlice/notice.slice';
import { TUserSliceState } from './store.types';
import { onboardingApi } from './apiSlice/onboarding.slice';
// template-flag-0

type TState =
  | CombinedState<{
      [x: string]: unknown;
      userSlice: TUserSliceState;
      authSlice: TAuthSliceState;
      appSlice: TAppSliceState;
    }>
  | undefined;

const appReducer = combineReducers({
  [tenantApi.reducerPath]: tenantApi.reducer,
  [animalApi.reducerPath]: animalApi.reducer,
  [paymentApi.reducerPath]: paymentApi.reducer,
  [docPacketApi.reducerPath]: docPacketApi.reducer,
  [animalTrainingApi.reducerPath]: animalTrainingApi.reducer,
  [leasePacketApi.reducerPath]: leasePacketApi.reducer,
  [residencyApi.reducerPath]: residencyApi.reducer,
  [policyApi.reducerPath]: policyApi.reducer,
  [fileApi.reducerPath]: fileApi.reducer,
  [curriculumApi.reducerPath]: curriculumApi.reducer,
  [storageApi.reducerPath]: storageApi.reducer,
  [applicationsApi.reducerPath]: applicationsApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [visitationApi.reducerPath]: visitationApi.reducer,
  [AuthAmplify.reducerPath]: AuthAmplify.reducer,
  [AuthAmplifyWithPhoneNumber.reducerPath]: AuthAmplifyWithPhoneNumber.reducer,
  [AuthAmplifyWithEmail.reducerPath]: AuthAmplifyWithEmail.reducer,
  [complaintApi.reducerPath]: complaintApi.reducer,
  [noticeApi.reducerPath]: noticeApi.reducer,
  [onboardingApi.reducerPath]: onboardingApi.reducer, // template-flag-1
  userSlice: userSlice.reducer,
  authSlice: authSlice.reducer,
  appSlice: appSlice.reducer,
});

const rootReducer = (state: TState, action: AnyAction) => {
  if (action.type === 'USER_LOGOUT') {
    return appReducer(undefined, action);
  }

  // @ts-ignore
  return appReducer(state, action); // here type CombinedState<{ [x: string]: never which does not  working correct
};

/*
 * A call site can opt out of the generic toast below when it has something more useful to say than
 * "couldn't reach the server" — an upload refused for its size looks exactly like a dropped
 * connection from here. The opt-out is read off the request's own arguments rather than off the
 * endpoint, so it stays with the one call that promised to show a message: a new caller of the
 * same endpoint keeps the generic toast instead of silently inheriting someone else's contract.
 *
 * The endpoint matcher is what types `originalArgs`, so the flag cannot drift from the endpoint
 * that declares it. It has to stay downstream of `isRejectedWithValue`: on its own it would also
 * match thrown, aborted and `condition` rejections, which carry no payload.
 */
const callerHandlesTransportError = (action: AnyAction): boolean =>
  storageApi.endpoints.uploadFile.matchRejected(action) &&
  action.meta.arg.originalArgs.handlesOwnTransportError === true;

export const rtkQueryErrorLogger: Middleware = () => next => action => {
  if (
    isRejectedWithValue(action) &&
    isTransportError(action.payload) &&
    !callerHandlesTransportError(action)
  ) {
    // shared key collapses simultaneous failures into a single toast
    toast({
      type: 'error',
      description: EText.NETWORK_ERROR,
      key: 'network-error',
    });
  }
  return next(action);
};

/*
 * The tenant animals page is two requests describing one thing: /animals for the animal cards and
 * /residency for the residency cards beneath them. They live in different api slices, so an animal
 * change invalidated one and left the other showing the previous state — a retracted application
 * still offering its actions, a converted animal still labelled with its old type.
 *
 * Mirroring it here rather than at each call site because the failure is silent and the list of
 * places that change an animal keeps growing. Any animalApi cache change counts: a fulfilled
 * mutation, or an explicit invalidateTags. Queries are excluded, or every read would trigger a
 * write. Over-invalidating is cheap — RTK Query only refetches a tag something is subscribed to.
 */
const ANIMAL_CACHE_CHANGES = [
  `${animalApi.reducerPath}/executeMutation/fulfilled`,
  `${animalApi.reducerPath}/invalidateTags`,
];

export const residencyAnimalsSyncMiddleware: Middleware =
  store => next => action => {
    const result = next(action);
    if (ANIMAL_CACHE_CHANGES.includes((action as AnyAction).type)) {
      store.dispatch(
        residencyApi.util.invalidateTags(['residency-with-animals']),
      );
    }
    return result;
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setupStore = (initialState: Record<string, any> = {}) =>
  configureStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: false,
      }).concat(
        tenantApi.middleware,
        animalApi.middleware,
        paymentApi.middleware,
        docPacketApi.middleware,
        animalTrainingApi.middleware,
        leasePacketApi.middleware,
        residencyApi.middleware,
        policyApi.middleware,
        storageApi.middleware,
        applicationsApi.middleware,
        fileApi.middleware,
        usersApi.middleware,
        curriculumApi.middleware,
        visitationApi.middleware,
        AuthAmplify.middleware,
        AuthAmplifyWithPhoneNumber.middleware,
        AuthAmplifyWithEmail.middleware,
        complaintApi.middleware,
        noticeApi.middleware,
        onboardingApi.middleware,
        rtkQueryErrorLogger,
        residencyAnimalsSyncMiddleware,
      ),
    preloadedState: initialState,
  });
export const store = setupStore();
export const clearAllCachedData = () => {
  store.dispatch(tenantApi.util.resetApiState());
  store.dispatch(animalApi.util.resetApiState());
  store.dispatch(paymentApi.util.resetApiState());
  store.dispatch(storageApi.util.resetApiState());
  store.dispatch(docPacketApi.util.resetApiState());
  store.dispatch(animalTrainingApi.util.resetApiState());
  store.dispatch(leasePacketApi.util.resetApiState());
  store.dispatch(policyApi.util.resetApiState());
  store.dispatch(residencyApi.util.resetApiState());
  store.dispatch(fileApi.util.resetApiState());
  store.dispatch(applicationsApi.util.resetApiState());
  store.dispatch(usersApi.util.resetApiState());
  store.dispatch(curriculumApi.util.resetApiState());
  store.dispatch(visitationApi.util.resetApiState());
  store.dispatch(AuthAmplify.util.resetApiState());
  store.dispatch(AuthAmplifyWithPhoneNumber.util.resetApiState());
  store.dispatch(AuthAmplifyWithEmail.util.resetApiState());
  store.dispatch(complaintApi.util.resetApiState());
  store.dispatch(noticeApi.util.resetApiState());
  store.dispatch(onboardingApi.util.resetApiState());
  // template-flag-3
};

export type RootState = ReturnType<typeof rootReducer>;
type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = cb =>
  useSelector(cb, shallowEqual);

setupListeners(store.dispatch);
